import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { useBoxConsumableActions } from "@modules/boxConsumable/hooks/useBoxConsumable"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { Plus, SquareMinus } from "lucide-react"
import { MenuItem } from "primereact/menuitem"
import { useTheme } from "styled-components"

type Props = {
  memberId: string
  consumableDetails: BoxConsumableDetailsEntity
  handleShowCreditModal: (consumableDetails: BoxConsumableDetailsEntity) => void
} & React.ComponentProps<"div">

export function MemberOfferItemConsumableMenu({ memberId, consumableDetails, handleShowCreditModal, ...props }: Props) {
  const theme = useTheme()
  const popup = useConfirmPopup()
  const { deletePurchase, isDeletePurchasePending } = useBoxConsumableActions(memberId)

  const canEditMemberOffer = useBoxStaffPermission(BoxStaffPermissions.EDIT_MEMBER_OFFER)
  const creditSessionIsVisible =
    consumableDetails.offer.type === BoxOfferType.TicketsBook && consumableDetails.count !== null ? true : false

  const removeConsumableIsVisible =
    consumableDetails.offer.type === BoxOfferType.Millenium && !!consumableDetails.count && consumableDetails.count > 0

  if (!canEditMemberOffer || (!creditSessionIsVisible && !removeConsumableIsVisible)) {
    return <></>
  }

  const handleCreditSession = () => {
    handleShowCreditModal(consumableDetails)
  }

  const handleRemoveSubscription = () => {
    popup.show({
      title: "",
      message: word("box.member.table.offer.item.popup.removeMessage"),
      accept: async () => {
        if (!isDeletePurchasePending) deletePurchase(consumableDetails.id)
      },
      footerProps: {
        align: true,
        confirmText: word("global.delete"),
        confirmClassName: "flex-1",
        cancelClassName: "flex-1",
        footerClassName: "flex flex-row justify-content-around w-full",
      },
    })
  }

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: word("box.member.table.offer.item.menu.creditSession"),
          icon: <Plus color={theme.textPrimary} strokeWidth={1} size={16} />,
          command: () => {
            handleCreditSession()
          },
          visible: creditSessionIsVisible,
        },
        {
          label: word("box.member.table.offer.item.menu.removeSubscription"),
          icon: <SquareMinus color={theme.textPrimary} strokeWidth={1} size={16} />,
          template: () => (
            <div
              className="flex align-items-center text-error gap-1 cursor-pointer hover:surface-100 transition-colors transition-duration-150"
              style={{
                paddingRight: "1.25rem",
                paddingLeft: "1.25rem",
                paddingTop: "0.75rem",
                paddingBottom: "0.75rem",
                borderRadius: "5px",
              }}
              onClick={handleRemoveSubscription}
            >
              <SquareMinus color={theme.error} strokeWidth={1} size={16} />
              <span style={{ marginLeft: "8px" }}>{word("box.member.table.offer.item.menu.removeSubscription")}</span>
            </div>
          ),
          visible: removeConsumableIsVisible,
        },
      ].filter((item) => item.visible),
    },
  ]

  return <EllipsisMenu items={ellipsisItems} {...props} />
}
